<script setup>
import { computed } from 'vue';
import { t } from '../i18n';
import { PANEL } from '../ui';
import {
  daysLabel, formatDay, formatForecast, formatLevel, formatRub, formatSignedLevel, forecastReason, isFresh, lastReached, progress,
} from '../achievements';
import InfoHint from './InfoHint.vue';

// Block 1 of «Достижения» (docs/achievements/SPEC.md, «Проект v2»): the next capital level in large type, how
// far it is and when it comes at the current pace; now, put in and what the market added; a level reached
// lately; the way to all levels.
const props = defineProps({
  // capital from the API: track + invested, market, forecast
  capital: {
    type: Object,
    required: true,
  },
  today: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['open']);

// Floor: «100 %» only when the level is reached.
const percent = computed(() => Math.floor(progress(props.capital) * 100));
// The bar runs from the last level (reached or there on the start) to the next one.
const from = computed(() => props.capital.levels.at(-1)?.level ?? 0);
const last = computed(() => lastReached(props.capital));
const fresh = computed(() => (isFresh(last.value?.date, props.today) ? last.value : null));
const count = computed(() => props.capital.levels.filter((l) => !l.at_start).length);

// Fell below the last level: the level stays reached (decision 2), said calmly.
const below = computed(() => {
  const top = props.capital.levels.at(-1)?.level ?? 0;
  return props.capital.current < top ? { level: top, sum: top - props.capital.current } : null;
});

const forecastText = computed(() => formatForecast(props.capital.forecast) || forecastReason(props.capital.forecast));
</script>

<template>
  <section :class="PANEL">
    <p class="text-[0.8125rem] text-slate-400">
      {{ t('achievements.nextTitle') }}
    </p>
    <template v-if="capital.next">
      <p class="mt-1 text-[2rem] font-semibold leading-tight tabular-nums text-slate-100 sm:text-[1.75rem]">
        {{ formatLevel(capital.next.level) }}
      </p>
      <div
        class="mt-3 h-2 overflow-hidden rounded-full bg-slate-800"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('achievements.nextTitle')"
      >
        <div
          class="h-full rounded-full bg-[#3987e5]"
          :style="{ width: `${percent}%` }"
        />
      </div>
      <p class="mt-1 flex justify-between gap-3 text-[0.75rem] tabular-nums text-slate-500">
        <span>{{ t('achievements.from', { level: formatLevel(from) }) }}</span>
        <span>{{ percent }} %</span>
      </p>
      <p class="mt-2 text-[0.9375rem] tabular-nums text-slate-200 sm:text-sm">
        {{ t('achievements.left', { sum: formatRub(Math.max(0, capital.next.remaining)) }) }}
      </p>
      <p
        v-if="forecastText"
        class="mt-0.5 flex items-center gap-1.5 text-[0.8125rem] text-slate-400"
      >
        <span>{{ forecastText }}</span>
        <InfoHint
          v-if="capital.forecast?.date"
          :label="t('achievements.forecastHintLabel')"
        >
          {{ t('achievements.forecastHint') }}
        </InfoHint>
      </p>
      <p
        v-if="below"
        class="mt-1 text-[0.8125rem] text-slate-400"
      >
        {{ t('achievements.belowLevel', { level: formatLevel(below.level), sum: formatLevel(below.sum) }) }}
      </p>
    </template>

    <dl class="mt-4 grid grid-cols-3 gap-3 border-t border-slate-800 pt-3 tabular-nums">
      <div class="min-w-0">
        <dt class="text-[0.75rem] text-slate-500">
          {{ t('achievements.now') }}
        </dt>
        <dd class="truncate text-[0.9375rem] text-slate-100 sm:text-sm">
          {{ formatLevel(capital.current) }}
        </dd>
      </div>
      <div class="min-w-0">
        <dt class="text-[0.75rem] text-slate-500">
          {{ t('achievements.invested') }}
        </dt>
        <dd class="truncate text-[0.9375rem] text-slate-100 sm:text-sm">
          {{ formatLevel(capital.invested) }}
        </dd>
      </div>
      <div class="min-w-0">
        <dt class="text-[0.75rem] text-slate-500">
          {{ t('achievements.market') }}
        </dt>
        <dd
          class="truncate text-[0.9375rem] sm:text-sm"
          :class="capital.market >= 0 ? 'text-emerald-400' : 'text-rose-400'"
        >
          {{ formatSignedLevel(capital.market) }}
        </dd>
      </div>
    </dl>

    <div
      v-if="fresh"
      class="mt-3 flex gap-2.5 border-t border-slate-800 pt-3"
    >
      <span
        class="mt-1.5 size-2 shrink-0 rounded-full bg-emerald-400"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="text-[0.9375rem] text-slate-100 sm:text-sm">
          {{ t('achievements.freshTitle', { level: formatLevel(fresh.level) }) }}
        </p>
        <p class="text-[0.8125rem] text-slate-400">
          {{ t('achievements.freshWhen', { date: formatDay(fresh.date), days: daysLabel(fresh.days) }) }}
        </p>
      </div>
    </div>

    <button
      v-if="count > 0"
      type="button"
      class="-mx-5 -mb-5 mt-4 flex h-12 w-[calc(100%+2.5rem)] items-center justify-between rounded-b-3xl border-t border-slate-800 px-5 text-[0.9375rem] text-slate-200 hover:bg-slate-800/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 sm:h-11 sm:rounded-b-lg sm:text-sm"
      @click="emit('open')"
    >
      <span>{{ t('achievements.allLevels', { count }) }}</span>
      <span
        class="text-slate-500"
        aria-hidden="true"
      >›</span>
    </button>
  </section>
</template>
